// 格式化工具：金额、日期、状态色、PAN 掩码
//
// 通用四函数（formatMoney / formatDateTime / formatDate / formatPercent）已收敛至
// @shared/formats/format（单一真源，消除双源；两端实现经 diff 确认行为一致），
// 此处 re-export 保持既有 `@/lib/format` import 路径不变。
// 以下仅保留 shared 未覆盖的 admin 专属展示工具（状态徽章 / 掩码 / 3DS / bps）。

export { formatDate, formatDateTime, formatMoney, formatPercent } from '@shared/formats/format';

/** 状态 → badge 样式映射（通用） */
export type BadgeKind = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const POSITIVE = new Set([
  'ACTIVE',
  'ENABLED',
  'APPROVED',
  'COMPLETED',
  'PAID',
  'VERIFIED',
  'RESOLVED',
  'READY',
  'FILED',
  'OK',
  'LIVE',
  'PUBLISHED',
  'SENT',
  'MATCHED',
  'DONE',
  'CREDITED',
  'approved',
  'REWRITTEN',
  'CLOSED',
]);
const NEGATIVE = new Set([
  'REJECTED',
  'FAILED',
  'FROZEN',
  'TERMINATED',
  'SUSPENDED',
  'DISABLED',
  'OVERDUE',
  'EXPIRED',
  'DECLINE',
  'DEAD',
  'ERROR',
  'CRIT',
]);
const PENDING = new Set([
  'PENDING',
  'PENDING_REVIEW',
  'DETECTED',
  'CONFIRMING',
  'PENDING_OPEN',
  'PENDING_APPROVAL',
  'pending',
  'SUBMITTED',
  'UNDER_REVIEW',
  'PROCESSING',
  'APPLIED',
  'QUEUED',
  'GENERATING',
  'DRAFT',
  'FIRING',
  'SENDING',
  'OPEN',
  'INVESTIGATING',
  'WARN',
  'MAINTENANCE',
  'NEED_INFO',
]);

export function statusBadge(status: string): BadgeKind {
  if (POSITIVE.has(status)) return 'success';
  if (NEGATIVE.has(status)) return 'danger';
  if (PENDING.has(status)) return 'warning';
  return 'neutral';
}

/** 风险等级 / 严重度 → 徽章 */
export function severityBadge(level: string): BadgeKind {
  switch (level) {
    case 'critical':
    case 'high':
    case 'P0':
    case 'P1':
      return 'danger';
    case 'medium':
    case 'P2':
      return 'warning';
    case 'low':
    case 'P3':
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * PAN 掩码：仅保留后四位，其余用 * 替换（数据红线：禁完整 PAN 默认展示）
 * @param pan 完整卡号或已部分掩码的字符串
 * @returns 形如 `**** **** **** 1234`
 */
export function maskPan(pan: string): string {
  if (!pan) return '—';
  // 已是掩码格式直接返回
  if (pan.includes('*')) return pan;
  const digits = pan.replace(/\D/g, '');
  if (digits.length < 4) return pan;
  const last4 = digits.slice(-4);
  return `**** **** **** ${last4}`;
}

/**
 * 邮箱掩码：保留首字符与域名，中间用 * 替换
 * 如 li***name@example.com
 */
export function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return email ?? '—';
  const [local, domain] = email.split('@');
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local.slice(-1)}@${domain}`;
}

/**
 * 手机号掩码：保留前 3 后 4
 * 如 +86 138****0001
 */
export function maskPhone(phone: string): string {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7) return phone;
  const prefix = phone.slice(0, phone.indexOf(digits[0]) + 3);
  const last4 = digits.slice(-4);
  return `${prefix}****${last4}`;
}

/**
 * 用户名/主体名掩码（持卡人、申请人等 PII）
 * 2 字符：首字+*；3+ 字符：首字 + ** + 尾字
 */
export function maskName(name: string): string {
  if (!name) return '—';
  if (name.length <= 1) return name;
  if (name.length === 2) return `${name[0]}*`;
  return `${name[0]}${'*'.repeat(Math.min(name.length - 2, 4))}${name.slice(-1)}`;
}

